import React from 'react';
import Tooltip from '@/components/atoms/Tooltip';
import { FiPackage, FiHash } from 'react-icons/fi';
import { BiBarcodeReader } from 'react-icons/bi';

import { Cell, PillCell, AppButton } from '@/components/atoms';
import { APP_SEMANTIC } from '@/constant/theme/colors.constant';
import { getDateTime, getDateTimeAgo } from '@/utils/dateTime';
import { getFormattedCurrency } from '@/utils/currency';
import type { ColumnDef } from '@/components/organism/Table';
import { PRODUCTS_PROPS } from '@/constant/product/product.const';
import { tid } from '@/utils/testId';
import { PRODUCTS_TEST_IDS as TID } from '@/constant/testIds/products.testIds';
import type { IProductProps } from './products.interface';

/* ── Columns ──────────────────────────────────────────────────────────────── */
export function buildColumns(
  isCopy: string,
  onCopyClick: (key: string) => void,
  handleMarketOpen: (details: any) => void,
  handleBarcodeOpen: (details: any, rowId: string) => void,
): ColumnDef<IProductProps>[] {
  return [
    {
      accessor: 'sourceOperatorId',
      header: PRODUCTS_PROPS.COLUMNS.OPERATOR_ID,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_OPERATOR_ID(`${idx}`))}
          value={(item as any).sourceOperatorId}
          variant="mono"
          copy={{ copyKey: `opid-${item.productId}`, isCopy, onCopyClick }}
        />
      ),
    },
    {
      accessor: 'sourceProductId',
      header: PRODUCTS_PROPS.COLUMNS.PRODUCT_ID,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_SOURCE_PRODUCT_ID(`${idx}`))}
          value={item.sourceProductId}
          tooltip={item.operatorProductId || item.sourceProductId}
          variant="mono"
          copy={{ copyKey: `pid-${item.productId}`, isCopy, onCopyClick }}
        />
      ),
    },
    {
      accessor: 'productCode',
      header: PRODUCTS_PROPS.COLUMNS.PRODUCT_CODE,
      sorting: true,
      render: (item, idx) => (
        <PillCell
          testId={tid(TID.COL_PRODUCT_CODE(`${idx}`))}
          value={item.productCode}
          icon={<FiHash className="w-3 h-3 flex-shrink-0" />}
          config={{
            bg: APP_SEMANTIC.neutral.bg,
            text: APP_SEMANTIC.neutral.text,
            border: APP_SEMANTIC.neutral.border,
          }}
          shape="badge"
          size="text-[10px]"
          className="font-mono font-bold border"
        />
      ),
    },
    {
      accessor: 'name',
      header: PRODUCTS_PROPS.COLUMNS.PRODUCT_NAME,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_NAME(`${idx}`))}
          value={item.name}
          tooltip
          variant="primary"
          copy={{ copyKey: `pname-${item.productId}`, isCopy, onCopyClick }}
          className="font-medium"
        />
      ),
    },
    {
      accessor: 'marketDetails',
      header: PRODUCTS_PROPS.COLUMNS.MARKETS,
      noTruncate: true,
      render: (item, idx) => (
        <AppButton
          data-testid={tid(TID.COL_MARKETS_BTN(`${idx}`))}
          variant="primary"
          tint
          size="xs"
          hoverBg={false}
          icon={<FiPackage className="w-3 h-3" />}
          onClick={(e) => {
            e.stopPropagation();
            handleMarketOpen(item.marketDetails);
          }}
        >
          View Markets
        </AppButton>
      ),
    },
    {
      accessor: 'barcodeDetails',
      header: PRODUCTS_PROPS.COLUMNS.BARCODES,
      noTruncate: true,
      render: (item, idx) => (
        <AppButton
          data-testid={tid(TID.COL_BARCODES_BTN(`${idx}`))}
          variant="purple"
          tint
          size="xs"
          hoverBg={false}
          icon={<BiBarcodeReader className="w-3.5 h-3.5" />}
          onClick={(e) => {
            e.stopPropagation();
            handleBarcodeOpen((item as any)?.product?.barcodes, `${idx}`);
          }}
        >
          View Barcodes
        </AppButton>
      ),
    },
    {
      accessor: 'cost',
      header: PRODUCTS_PROPS.COLUMNS.COST,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_COST(`${idx}`))}
          value={String(
            getFormattedCurrency((item as any)?.cost).formatted ?? '',
          )}
          className="font-semibold"
        />
      ),
    },
    {
      accessor: 'price',
      header: PRODUCTS_PROPS.COLUMNS.PRICE,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_PRICE(`${idx}`))}
          value={String(
            getFormattedCurrency((item as any)?.price).formatted ?? '',
          )}
          className="font-semibold"
        />
      ),
    },
    {
      accessor: 'deletedOn',
      header: PRODUCTS_PROPS.COLUMNS.STATUS,
      render: (item, idx) => (
        <Tooltip
          content={
            item.deletedOn
              ? `Deleted: ${getDateTime(item.deletedOn)}`
              : 'Active'
          }
          placement="right"
        >
          <div>
            <PillCell
              testId={tid(TID.COL_STATUS(`${idx}`))}
              value={item.deletedOn ? 'Inactive' : 'Active'}
              preset={item.deletedOn ? 'inactive' : 'active'}
              size="text-[11px]"
              className="border font-semibold cursor-default"
            />
          </div>
        </Tooltip>
      ),
    },
    {
      accessor: 'updatedOn',
      header: PRODUCTS_PROPS.COLUMNS.UPDATED,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_UPDATED_ON(`${idx}`))}
          value={item.updatedOn}
          variant="muted"
          tooltip={getDateTime(item.updatedOn)}
          tooltipPlacement="top"
          size="text-[11px]"
        >
          {getDateTimeAgo(item.updatedOn)}
        </Cell>
      ),
    },
    {
      accessor: 'createdOn',
      header: PRODUCTS_PROPS.COLUMNS.CREATED,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_CREATED_ON(`${idx}`))}
          value={item.createdOn}
          format="datetime"
          variant="muted"
          size="text-[11px]"
        />
      ),
    },
  ];
}
